import type { Editor } from '@tiptap/react';
import {
  BoldIcon,
  ItalicIcon,
  ListIcon,
  ListOrderedIcon,
  StrikethroughIcon,
  UnderlineIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';

interface BriefSectionFormatBarProps {
  /** The section's own TipTap instance. Every command runs against this one. */
  editor: Editor;
}

/**
 * Formatting for a prose section, surfaced only while that section is the one being edited and
 * placed outside the text column so it never reflows the prose it formats.
 *
 * Measured against `.local/prototypes/mf-brief-improvements-150926.html`: 28px to the left of
 * the section box, vertically centred on it, 24px wide, 18px controls with 11px glyphs on a
 * card surface with a light border, a 6px radius and the small shadow.
 *
 * This replaces a permanent toolbar above the document that also carried undo, redo and a
 * block-style dropdown. The prototype surfaces none of those, so their buttons are gone; the
 * commands themselves are untouched and still reachable from the keyboard, since StarterKit
 * keeps its history and heading input rules.
 */
export function BriefSectionFormatBar({ editor }: BriefSectionFormatBarProps) {
  const { t } = useTranslation('projects');

  const marks = [
    { key: 'bold', label: t('projectBriefDetail.briefSectionFormatBar.bold'), Icon: BoldIcon },
    {
      key: 'italic',
      label: t('projectBriefDetail.briefSectionFormatBar.italic'),
      Icon: ItalicIcon,
    },
    {
      key: 'underline',
      label: t('projectBriefDetail.briefSectionFormatBar.underline'),
      Icon: UnderlineIcon,
    },
    {
      key: 'strike',
      label: t('projectBriefDetail.briefSectionFormatBar.strikethrough'),
      Icon: StrikethroughIcon,
    },
  ] as const;

  const lists = [
    {
      key: 'bulletList',
      label: t('projectBriefDetail.briefSectionFormatBar.bulletList'),
      Icon: ListIcon,
    },
    {
      key: 'orderedList',
      label: t('projectBriefDetail.briefSectionFormatBar.orderedList'),
      Icon: ListOrderedIcon,
    },
  ] as const;

  const run = (command: string) => () => {
    const chain = editor.chain().focus() as unknown as Record<string, () => { run: () => void }>;
    chain[command]().run();
  };

  const COMMAND: Record<string, string> = {
    bold: 'toggleBold',
    italic: 'toggleItalic',
    underline: 'toggleUnderline',
    strike: 'toggleStrike',
    bulletList: 'toggleBulletList',
    orderedList: 'toggleOrderedList',
  };

  const renderButton = ({
    key,
    label,
    Icon,
  }: {
    key: string;
    label: string;
    Icon: typeof BoldIcon;
  }) => (
    <Tooltip key={key}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-pressed={editor.isActive(key)}
          // `onMouseDown` preventDefault keeps the caret in the prose: without it the button
          // takes DOM focus first and the command applies to an empty selection.
          onMouseDown={(event) => event.preventDefault()}
          onClick={run(COMMAND[key])}
          className="flex size-4.5 items-center justify-center rounded-sm text-foreground hover:bg-accent aria-pressed:bg-accent"
        >
          <Icon className="size-3" />
        </button>
      </TooltipTrigger>
      {}
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  );

  return (
    <TooltipProvider>
      <div
        data-testid="brief-section-format-bar"
        className="absolute -left-7 top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-px rounded-md border border-border-light bg-card p-0.5 shadow-sm"
      >
        {marks.map(renderButton)}
        <span aria-hidden className="my-px h-px w-3 bg-border-light" />
        {lists.map(renderButton)}
      </div>
    </TooltipProvider>
  );
}
